/* Uit Krabbelpaal.dc.html; bodies letterlijk overgenomen. */

import { GH, GW } from '../lib/config.js';
import { PALETTE_HEX } from '../lib/palette.js';

export default {
  getCtx() { return this.canvasRef.current && this.canvasRef.current.getContext('2d'); },

  getPos(e) {
      const rect = this.canvasRef.current.getBoundingClientRect();
      return {
        x: Math.floor((e.clientX - rect.left) * (GW / rect.width)),
        y: Math.floor((e.clientY - rect.top) * (GH / rect.height))
      };
    },

  floodFill(x, y, fillColorHex) {
      const ctx = this.getCtx();
      const w = GW, h = GH;
      const imgData = ctx.getImageData(0, 0, w, h);
      const data = imgData.data;
      if (x < 0 || y < 0 || x >= w || y >= h) return;
      const v = parseInt(fillColorHex.slice(1), 16);
      const fr = (v >> 16) & 255, fg = (v >> 8) & 255, fb = v & 255;
      const idx = (px, py) => (py * w + px) * 4;
      const s = idx(x, y);
      const tr = data[s], tg = data[s + 1], tb = data[s + 2], ta = data[s + 3];
      if (tr === fr && tg === fg && tb === fb && ta === 255) return;
      const matches = (i) => data[i] === tr && data[i + 1] === tg && data[i + 2] === tb && data[i + 3] === ta;
      const stack = [[x, y]];
      while (stack.length) {
        const [px, py] = stack.pop();
        if (px < 0 || py < 0 || px >= w || py >= h) continue;
        const i = idx(px, py);
        if (!matches(i)) continue;
        data[i] = fr; data[i + 1] = fg; data[i + 2] = fb; data[i + 3] = 255;
        stack.push([px + 1, py], [px - 1, py], [px, py + 1], [px, py - 1]);
      }
      ctx.putImageData(imgData, 0, 0);
    },

  paintBlock(x, y, colorHex) {
      const ctx = this.getCtx();
      const n = this.state.brushSize;
      // Dikkere kwast rond de cursor: bij een oneven maat precies gecentreerd,
      // bij een even maat een halve cel naar linksboven.
      const off = Math.floor((n - 1) / 2);
      const bx = x - off;
      const by = y - off;
      if (colorHex === null) ctx.clearRect(bx, by, n, n);
      else { ctx.fillStyle = colorHex; ctx.fillRect(bx, by, n, n); }
    },

  spray(x, y, colorHex) {
      const ctx = this.getCtx();
      const r = this.state.brushSize + 1;
      ctx.fillStyle = colorHex;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d > r) continue;
          if (Math.random() > 0.5 - (d / r) * 0.34) continue;
          const px = x + dx, py = y + dy;
          if (px < 0 || py < 0 || px >= GW || py >= GH) continue;
          ctx.fillRect(px, py, 1, 1);
        }
      }
    },

  applyTool(x, y, color) {
      if (this.rightErase) this.paintBlock(x, y, null);
      else if (this.state.tool === 'pencil') this.paintBlock(x, y, color);
      else if (this.state.tool === 'eraser') this.paintBlock(x, y, null);
      else if (this.state.tool === 'spray') this.spray(x, y, color);
    },

  // De muis meldt zich maar zo'n zestig keer per seconde: beweeg je snel, dan
  // slaat hij cellen over. Alles tussen de vorige en de nieuwe cel vullen we
  // zelf op, zodat een vlugge streek net zo dicht is als een langzame.
  strokeTo(x, y, color) {
      const from = this.lastPaint;
      if (!from) { this.applyTool(x, y, color); this.lastPaint = { x, y }; return; }
      if (from.x === x && from.y === y) return;
      const dx = Math.abs(x - from.x), dy = Math.abs(y - from.y);
      const sx = from.x < x ? 1 : -1, sy = from.y < y ? 1 : -1;
      let err = dx - dy, cx = from.x, cy = from.y;
      for (let guard = 0; guard < 4096; guard++) {
        if (cx !== from.x || cy !== from.y) this.applyTool(cx, cy, color);
        if (cx === x && cy === y) break;
        const e2 = 2 * err;
        if (e2 > -dy) { err -= dy; cx += sx; }
        if (e2 < dx) { err += dx; cy += sy; }
      }
      this.lastPaint = { x, y };
    },

  // Lijn, rechthoek en cirkel werken hetzelfde: slepen van hoek naar hoek,
  // elke beweging opnieuw vanaf het beeld van vóór de vorm.
  isShapeTool(t) { return t === 'line' || t === 'rect' || t === 'circle'; },

  drawShape(x0, y0, x1, y1, color) {
      if (this.state.tool === 'rect') this.drawRect(x0, y0, x1, y1, color);
      else if (this.state.tool === 'circle') this.drawEllipse(x0, y0, x1, y1, color);
      else this.drawLine(x0, y0, x1, y1, color);
    },

  // Shift ingedrukt: een lijn klikt vast op stappen van 45°, en een rechthoek
  // of cirkel wordt precies vierkant of rond. Het zusje van de spatiebalk, die
  // de vorm verschuift in plaats van vervormt.
  constrain(from, x, y) {
      const dx = x - from.x, dy = y - from.y;
      const sx = dx < 0 ? -1 : 1, sy = dy < 0 ? -1 : 1;
      let ax = Math.abs(dx), ay = Math.abs(dy);
      if (this.state.tool === 'line') {
        // De grens tussen recht en schuin ligt op 22,5°; daar hoort deze factor bij.
        const T = 1 + Math.SQRT2;
        if (ax > ay * T) return { x, y: from.y };
        if (ay > ax * T) return { x: from.x, y };
      }
      // Eén maat voor beide kanten. Niet verder dan de rand: een afgesneden
      // vierkant is geen vierkant meer.
      let n = this.state.tool === 'line' ? Math.round((ax + ay) / 2) : Math.max(ax, ay);
      n = Math.min(n, sx < 0 ? from.x : GW - 1 - from.x, sy < 0 ? from.y : GH - 1 - from.y);
      return { x: from.x + sx * n, y: from.y + sy * n };
    },

  // Het doek van vóór de vorm terugzetten en de vorm opnieuw trekken. Ook nodig
  // als alleen shift verandert terwijl de muis stilstaat.
  redrawShape(x, y) {
      if (!this.lineFrom || !this.lineBase) return;
      const p = this.shiftHeld ? this.constrain(this.lineFrom, x, y) : { x, y };
      this.getCtx().putImageData(this.lineBase, 0, 0);
      this.drawShape(this.lineFrom.x, this.lineFrom.y, p.x, p.y, PALETTE_HEX[this.state.primaryIndex]);
    },

  onShift(down) {
      if (this.shiftHeld === down) return;
      this.shiftHeld = down;
      if (!this.state.drawing || !this.isShapeTool(this.state.tool) || !this.lastCur) return;
      this.redrawShape(this.lastCur.x, this.lastCur.y);
      this.duoTick();
    },

  drawRect(x0, y0, x1, y1, color) {
      const ax = Math.min(x0, x1), bx = Math.max(x0, x1);
      const ay = Math.min(y0, y1), by = Math.max(y0, y1);
      for (let px = ax; px <= bx; px++) { this.paintBlock(px, ay, color); this.paintBlock(px, by, color); }
      for (let py = ay; py <= by; py++) { this.paintBlock(ax, py, color); this.paintBlock(bx, py, color); }
    },

  // Cirkel: per kolom én per rij het randpunt zoeken, zo blijft de rand
  // aan elkaar vast zitten, ook bij een platte ovaal.
  drawEllipse(x0, y0, x1, y1, color) {
      const ax = Math.min(x0, x1), bx = Math.max(x0, x1);
      const ay = Math.min(y0, y1), by = Math.max(y0, y1);
      const cx = (ax + bx) / 2, cy = (ay + by) / 2;
      const rx = (bx - ax) / 2, ry = (by - ay) / 2;
      if (rx < 0.5 || ry < 0.5) { this.drawRect(x0, y0, x1, y1, color); return; }
      const seen = {};
      const put = (px, py) => {
        const k = px + ',' + py;
        if (seen[k]) return;
        seen[k] = 1;
        this.paintBlock(px, py, color);
      };
      const clamp = (v) => Math.max(-1, Math.min(1, v));
      for (let px = ax; px <= bx; px++) {
        const t = clamp((px - cx) / rx);
        const d = ry * Math.sqrt(Math.max(0, 1 - t * t));
        put(px, Math.round(cy - d)); put(px, Math.round(cy + d));
      }
      for (let py = ay; py <= by; py++) {
        const t = clamp((py - cy) / ry);
        const d = rx * Math.sqrt(Math.max(0, 1 - t * t));
        put(Math.round(cx - d), py); put(Math.round(cx + d), py);
      }
    },

  // Rechte lijn: van het startpunt naar waar je nu bent, elke beweging opnieuw
  // vanaf het beeld van vóór de lijn getekend.
  drawLine(x0, y0, x1, y1, color) {
      const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
      const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
      let err = dx - dy, x = x0, y = y0;
      for (let guard = 0; guard < 4096; guard++) {
        this.paintBlock(x, y, color);
        if (x === x1 && y === y1) break;
        const e2 = 2 * err;
        if (e2 > -dy) { err -= dy; x += sx; }
        if (e2 < dx) { err += dx; y += sy; }
      }
    },

  // Pen: klik punten aan, de lijn loopt er vloeiend doorheen. Klik het laatste
  // punt nog eens (of Enter/Escape) om af te ronden, het eerste punt om te sluiten.
  // Pad naar pixels: dicht bemonsteren, dubbele buren weghalen, zodat de lijn
  // overal precies één pixel dik blijft.
  penCells(pts, closed) {
      const cells = [];
      const push = (fx, fy) => {
        const x = this.clampG(Math.round(fx)), y = this.clampGY(Math.round(fy));
        const l = cells[cells.length - 1];
        if (l && l.x === x && l.y === y) return;
        if (l && (Math.abs(x - l.x) > 1 || Math.abs(y - l.y) > 1)) {
          const dx = Math.abs(x - l.x), dy = Math.abs(y - l.y);
          const sx = l.x < x ? 1 : -1, sy = l.y < y ? 1 : -1;
          let err = dx - dy, cx = l.x, cy = l.y;
          for (let g = 0; g < 4096; g++) {
            const e2 = 2 * err;
            if (e2 > -dy) { err -= dy; cx += sx; }
            if (e2 < dx) { err += dx; cy += sy; }
            if (cx === x && cy === y) break;
            cells.push({ x: cx, y: cy });
          }
        }
        cells.push({ x, y });
      };
      const seg = (a, b) => {
        const p1x = a.x + (a.hx || 0), p1y = a.y + (a.hy || 0);
        const p2x = b.x - (b.hx || 0), p2y = b.y - (b.hy || 0);
        const span = Math.abs(b.x - a.x) + Math.abs(b.y - a.y) + Math.abs(a.hx || 0) + Math.abs(a.hy || 0) + Math.abs(b.hx || 0) + Math.abs(b.hy || 0);
        const steps = Math.max(12, span * 6);
        for (let s = 0; s <= steps; s++) {
          const t = s / steps, u = 1 - t;
          push(u * u * u * a.x + 3 * u * u * t * p1x + 3 * u * t * t * p2x + t * t * t * b.x,
               u * u * u * a.y + 3 * u * u * t * p1y + 3 * u * t * t * p2y + t * t * t * b.y);
        }
      };
      for (let i = 0; i < pts.length - 1; i++) seg(pts[i], pts[i + 1]);
      if (closed && pts.length > 2) seg(pts[pts.length - 1], pts[0]);
      if (!cells.length) return cells;
      // Een pixel die tussen twee 8-verbonden buren in ligt, is overbodig.
      const out = [cells[0]];
      for (let i = 1; i < cells.length; i++) {
        const last = out[out.length - 1], nxt = cells[i + 1];
        if (nxt && Math.abs(nxt.x - last.x) <= 1 && Math.abs(nxt.y - last.y) <= 1) continue;
        if (cells[i].x === last.x && cells[i].y === last.y) continue;
        out.push(cells[i]);
      }
      return out;
    },

  renderPen(preview) {
      if (!this.penPts || !this.penBase) return;
      const ctx = this.getCtx();
      ctx.putImageData(this.penBase, 0, 0);
      const pts = preview ? this.penPts.concat([preview]) : this.penPts;
      const color = PALETTE_HEX[this.state.primaryIndex];
      if (pts.length === 1) this.paintBlock(pts[0].x, pts[0].y, color);
      else this.penCells(pts, !!this.penClosed).forEach((c) => this.paintBlock(c.x, c.y, color));
      this.duoTick();
    },

  // Tijdens het buigen van een punt luistert het hele venster mee, zodat je
  // handvat ook buiten het canvas mag komen.
  penDocMove(e) {
      if (this.penDrag == null || !this.penPts || !this.canvasRef.current) return;
      const src = e.touches && e.touches[0] ? e.touches[0] : e;
      const { x, y } = this.getPos(src);
      const a = this.penPts[this.penDrag];
      a.hx = x - a.x; a.hy = y - a.y;
      this.renderPen(null);
    },

  penDocUp() { this.penDrag = null; this.penDragOff(); },

  penDragOn() {
      if (this.penDocBound) return;
      this.penDocBound = true;
      window.addEventListener('mousemove', this.penDocMove);
      window.addEventListener('mouseup', this.penDocUp);
      window.addEventListener('touchmove', this.penDocMove, { passive: true });
      window.addEventListener('touchend', this.penDocUp);
    },

  penDragOff() {
      if (!this.penDocBound) return;
      this.penDocBound = false;
      window.removeEventListener('mousemove', this.penDocMove);
      window.removeEventListener('mouseup', this.penDocUp);
      window.removeEventListener('touchmove', this.penDocMove);
      window.removeEventListener('touchend', this.penDocUp);
    },

  finishPen() {
      this.penDragOff();
      if (!this.penPts) return;
      this.renderPen(null);
      this.penPts = null; this.penBase = null; this.penClosed = false; this.penDrag = null;
      this.snapshotCanvas();
      this.setState({ drawing: false });
    }
};
